import { Track } from "@/src/lib/tracking";
import { RGBColor } from "d3";
import { IAddShadowParams, IAddSignalPointParams, SVGEl } from "./type";
import {
  drawLine,
  getScale,
  handleChartResize,
  getSizes,
  getSVG,
  createOrSelectGroup,
} from "./utils";

// 创建带信号的简易折线图
const createEasyLineChart = (params: {
  data: Array<number>;
  el: HTMLElement;
  ticks?: Array<{ x: number; color: string }>;
  lineColor: string;
  shadow: boolean;
  /** 线条宽度，默认值为 2 */
  lineWidth?: number;
}) => {
  const d3 = (window as any)?.d3;
  const canUseD3 = !!d3?.select;

  if (!params.data || params.data.length === 0) {
    Track.log("error", "createEasyLineChart", "data cannot be empty");
    return null;
  }
  if (!params.el) {
    Track.log("error", "createEasyLineChart", "el cannot be empty");
    return null;
  }
  if (!canUseD3) {
    Track.log("warn", "createEasyLineChart", "d3 is unavailable, skip drawing");
    return null;
  }
  const isValidData = params.data.every((d) => typeof d === "number");
  if (!isValidData) {
    Track.log("error", "createEasyLineChart", "data must be number");
    return null;
  }

  // 添加阴影
  const addShadow = ({
    svg,
    xScale,
    yScale,
    data,
    color,
  }: Omit<IAddShadowParams, "segments" | "name" | "svg"> & {
    data: Array<number>;
    color: string;
    svg: SVGEl;
  }) => {
    const id = crypto.randomUUID().slice(0, 8);
    // 创建渐变
    const gradient = svg
      .append("defs")
      .append("linearGradient")
      .attr("id", `area-gradient-${id}`)
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    // 添加渐变色标
    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", color)
      .attr("stop-opacity", 0.05);

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", color)
      .attr("stop-opacity", 0);
    // 创建区域生成器
    const areaGenerator = d3
      .area()
      .x((_d: number, i: number) => xScale(i))
      .y0((d: number) => yScale(d))
      .y1(() => yScale(yScale.domain()[0])); // 或者使用其他合适的基线值
    // 绘制区域
    svg
      .append("path")
      .datum(data)
      .attr("d", areaGenerator)
      .attr("fill", `url(#area-gradient-${id})`)
      .attr("stroke", "none");
  };

  // 添加信号点
  const addSignalPoint = ({
    xScale,
    yScale,
    svg,
    data,
  }: IAddSignalPointParams) => {
    if (!data || data.length === 0) {
      Track.log(
        "error",
        "createEasyLineChart",
        "signalPoint data cannot be empty",
      );
      return null;
    }
    const isValidData = data.every((d) => typeof d.x === "number" && !!d.color);
    if (!isValidData) {
      Track.log(
        "error",
        "createEasyLineChart",
        "signalPoint data must be number",
      );
      return null;
    }
    const g = svg.append("g").attr("class", "signal-point");
    const defs = g.append("defs");
    data.forEach((d) => {
      const filterId = `drop-shadow-glow-${d.x}`;
      // 定义滤镜
      const filter = defs
        .append("filter")
        .attr("id", filterId)
        .attr("filterUnits", "userSpaceOnUse")
        .attr("color-interpolation-filters", "sRGB");

      // 高斯模糊
      filter
        .append("feGaussianBlur")
        .attr("in", "SourceAlpha")
        .attr("stdDeviation", 10) // 模糊程度
        .attr("result", "blur");
      const color = d3.color(d.color);
      if (!color) {
        Track.log(
          "error",
          "createEasyLineChart",
          "signalPoint color is invalid",
        );
        return;
      }
      const r = (color as RGBColor).r / 255;
      const g = (color as RGBColor).g / 255;
      const b = (color as RGBColor).b / 255;
      // 颜色矩阵，用于给模糊后的图形上色
      filter
        .append("feColorMatrix")
        .attr("in", "blur")
        .attr("type", "matrix")
        // 将模糊的alpha通道转换为特定颜色，并设置不透明度
        .attr(
          "values",
          `0 0 0 0 ${r.toFixed(8)}
                         0 0 0 0 ${g.toFixed(8)}
                         0 0 0 0 ${b.toFixed(8)}
                         0 0 0 0.6 0`,
        )
        .attr("result", "coloredBlur");

      // 合并原始图形和带颜色的模糊效果
      const feMerge = filter.append("feMerge");
      feMerge.append("feMergeNode").attr("in", "coloredBlur");
      feMerge.append("feMergeNode").attr("in", "SourceGraphic");
    });

    // 绘制信号点
    g.selectAll(".signal-point")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "signal-point")
      .attr("cx", (d) => xScale(d.x))
      .attr("cy", (d) => yScale(params.data[d.x]))
      .attr("r", 3) // 圆点半径
      .attr("fill", (d) => d.color)
      // .attr("opacity", 0.3);
      .attr("filter", (d) => `url(#drop-shadow-glow-${d.x})`);
    // 绘制信号点
    g.selectAll(".signal-point-dashed")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "signal-point-dashed")
      .attr("cx", (d) => xScale(d.x))
      .attr("cy", (d) => yScale(params.data[d.x]))
      .attr("r", 5.325) // 圆点半径
      .attr("stroke", "var(--edgen-content\_primary)")
      .attr("stroke-dasharray", "2 2")
      .attr("stroke-linecap", "round")
      .attr("stroke-width", ".75")
      .attr("fill", "none");
    // .attr("opacity", 0.3);
    // .attr("filter", (d) => `url(#drop-shadow-glow-${d.x})`);
  };

  /**
   * 绘制图表的主函数
   * 负责创建和更新图表的所有元素
   */
  const drawChart = () => {
    // 清除之前的图表内容
    d3.select(params.el).selectAll("*").remove();
    // 获取图表可用尺寸
    const { containerHeight, containerWidth } = getSizes(params.el);
    // 如果图表可用尺寸为0，直接返回 不需要绘制
    if (containerHeight === 0 || containerWidth === 0) {
      return;
    }
    // 创建SVG
    const svg = getSVG(params.el);
    if (!svg) {
      return null;
    }
    // 获取比例尺
    const xScale = getScale(
      [0, params.data.length - 1],
      [6, containerWidth - 6],
    );
    const yScale = getScale(
      [d3.min(params.data)!, d3.max(params.data)!],
      [containerHeight - 6, 6],
    );
    // 添加阴影
    addShadow({
      svg,
      xScale,
      yScale,
      data: params.data,
      color: params.lineColor,
    });
    // 折线组
    const lineGroup = createOrSelectGroup(svg, "line");
    drawLine({
      svg: lineGroup,
      xScale,
      yScale,
      line: [
        {
          data: params.data.map((d, i) => ({ x: i, y: d })),
        },
      ],
      color: params.lineColor,
      lineWidth: params.lineWidth,
    });
    // 添加折线
    // drawLine({
    //   svg: lineGroup,
    //   xScale,
    //   yScale,
    //   data: params.data,
    //   color: params.lineColor,
    // });
    // 添加信号点
    addSignalPoint({ svg, xScale, yScale, data: params.ticks });
  };

  // 清理画布
  const clearSvg = () => {
    d3.select(params.el).selectAll("*").remove();
  };

  // 处理图表自适应尺寸
  const { cleanup: handleChartResizeCleanup } = handleChartResize({
    el: params.el,
    drawChart,
    clearSvg,
  });

  return {
    cleanup: () => {
      handleChartResizeCleanup();
      clearSvg();
    },
  };
};

export default createEasyLineChart;
